import React, { useState } from "react"
import PropTypes from "prop-types"
import { Form, Checkbox, Button, Flex } from "antd"
import { DownOutlined, UpOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { Text } from "ui/Typography"
import DataTable from "ui/DataTable"
import { ConfirmModal } from "ui/ConfirmModal"
import useScheduleTimeOffForm from "./hooks/useScheduleTimeOffForm"
import useScheduledTimeOffColumns from "./hooks/useScheduledTimeOffColumns"
import DateRangeFields from "./components/DateRangeFields"
import TimeRangeFields from "./components/TimeRangeFields"
import ModalFooter from "./components/ModalFooter"
import styles from "./index.module.css"

const ScheduleTimeOffModal = ({
  open,
  onCancel,
  onSave,
  onSaveAndAddNew,
  editMode = false,
  initialValues,
  timeOffData = [],
  onDeleteTimeOff
}) => {
  const { t } = useTranslation()

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState(null)

  // Form state and handlers
  const {
    form,
    isPartOfDay,
    setIsPartOfDay,
    showScheduledTimeOff,
    setShowScheduledTimeOff,
    isSaveEnabled,
    handleCancel,
    handleFinish,
    handleSaveAndAddNew
  } = useScheduleTimeOffForm(onSave, onSaveAndAddNew, onCancel)

  // Edit/Delete handlers for table
  const handleEdit = (record) => {
    // TODO: Implement edit functionality
    console.log("Edit time off", record)
  }

  const handleDelete = (record) => {
    setRecordToDelete(record)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = () => {
    if (recordToDelete && onDeleteTimeOff) {
      onDeleteTimeOff(recordToDelete)
    }
    setDeleteConfirmOpen(false)
    setRecordToDelete(null)
  }

  const cancelDelete = () => {
    setDeleteConfirmOpen(false)
    setRecordToDelete(null)
  }

  // Table columns
  const columns = useScheduledTimeOffColumns(handleEdit, handleDelete)

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        title={editMode ? t("crewMember.scheduledTimeOff.editTitle") : t("crewMember.scheduledTimeOff.title")}
        width={800}
        className={styles.modal}
        centered
      >
        <Flex vertical gap={24} className="py-s">
          <Form form={form} onFinish={handleFinish} layout="vertical" initialValues={initialValues}>
            {/* Date Range Fields */}
            <DateRangeFields />

            {/* Part of Day Checkbox */}
            <div className="py-xxs mb-xl">
              <Checkbox
                checked={isPartOfDay}
                onChange={(e) => setIsPartOfDay(e.target.checked)}
                className={styles.partOfDayCheckbox}
              >
                {t("crewMember.scheduledTimeOff.fields.partOfDay")}
              </Checkbox>
            </div>

            {/* Time Range Fields */}
            <TimeRangeFields isPartOfDay={isPartOfDay} />

            {/* Display Scheduled Time Off Button */}
            <div className="mb-m">
              <Button
                type="text"
                className={styles.displayScheduledButton}
                onClick={() => setShowScheduledTimeOff(!showScheduledTimeOff)}
                icon={showScheduledTimeOff ? <UpOutlined /> : <DownOutlined />}
                iconPosition="end"
                color="primary"
                variant="outlined"
              >
                {showScheduledTimeOff
                  ? t("crewMember.scheduledTimeOff.buttons.hideScheduled")
                  : t("crewMember.scheduledTimeOff.buttons.displayScheduled")}
              </Button>
            </div>

            {/* Scheduled Time Off Table */}
            {showScheduledTimeOff && (
              <Flex vertical className={styles.scheduledTimeOffTable}>
                <Text size="lg" fw="600" className="mb-m">
                  {t("crewMember.scheduledTimeOff.table.title")}
                </Text>
                <DataTable columns={columns} dataSource={timeOffData} pagination={false} rowClickKey="id" />
              </Flex>
            )}
          </Form>

          {/* Modal Footer */}
          <ModalFooter
            editMode={editMode}
            isSaveEnabled={isSaveEnabled}
            onCancel={handleCancel}
            onSaveAndAddNew={handleSaveAndAddNew}
            onSubmit={() => form.submit()}
          />
        </Flex>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title={t("crewMember.scheduledTimeOff.deleteConfirmation.title")}
        info={t("crewMember.scheduledTimeOff.deleteConfirmation.message")}
        onCancel={cancelDelete}
        onDelete={confirmDelete}
        deleteLabel={t("crewMember.scheduledTimeOff.table.actions.delete")}
        cancelLabel={t("crewMember.scheduledTimeOff.buttons.cancel")}
      />
    </>
  )
}

ScheduleTimeOffModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveAndAddNew: PropTypes.func,
  editMode: PropTypes.bool,
  initialValues: PropTypes.object,
  timeOffData: PropTypes.array,
  onDeleteTimeOff: PropTypes.func
}

export default ScheduleTimeOffModal
